import React, { useCallback, useState, useEffect, Fragment } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';

import CheckoutOrderingRegisterStep from './CheckoutOrderingRegisterStep';
import CheckoutOrderingAddressStep from './CheckoutOrderingAddressStep';
import CheckoutOrderingPaymentStep from './CheckoutOrderingPaymentStep';
import LoadingView from '../LoadingView';
import withRequestHandler from '../../hoc/withRequestHandler';
import CartNavigationSteps from '../cart-items/CartNavigationSteps';
import MobileContainer from '../../../components/misc/MobileContainer';
import StickyStayInPlaceContainer from '../../../components/misc/StickyStayInPlaceContainer';
import { CartQuickCheckout } from '../../../components/content/cart-quick-checkout';
import DvdInforationDialog from '../dialogs/DvdInforationDialog';

import {
  BraintreeClientTokenMutation,
  ConfirmCheckoutMutation,
  CreateOrderMutation,
  CreateOrUpdateOrderWithPaypalExpressMutation,
  FinishPaypalExpressPaymentMutation,
  PayWithBankTransferMutation,
  PayWithPaypalMutation,
  RequestLoginMutation,
  SetOrderAddressMutation,
  SetOrderShipmentAddressWithNewAddressMutation,
} from '../../../mutations';

import { initPaypalButton } from '../../../utils/initPaypalButton';
import { includesPayPal, getRealAmount } from '../../../utils/common';
import { setToLocalStorage } from '../../../utils/browser-storage';
import { trackEvent } from '../../../utils/ga-tracking';

import {
  REGISTER_STEP,
  ADDRESS_STEP,
  PAYMENT_STEP,
  MAGIC_LINK_SENT_MSG,
  PAYPAL,
  BANKTRANSFER,
  ADYEN,
} from '../../../utils/variables';

const CheckoutOrderingSteps = (props) => {
  const { cart, isLoggedIn, isLoading, viewer, onShowCart, onOpenNotificationDialog, history, couponShouldBeRemoved, apiRedirectToPayment } = props;

  const determineFirstStep = () => {
    if (apiRedirectToPayment && isLoggedIn && cart.order) {
      return PAYMENT_STEP;
    } else if (cart.order || isLoggedIn) {
      return ADDRESS_STEP;
    } else {
      return REGISTER_STEP;
    }
  };

  const [hasPayPal] = useState(includesPayPal(cart.availablePaymentMethods));
  const [hasPayPalCheckoutBtn, setHasPayPalCheckoutBtn] = useState(false);
  const [isLoadingPayPalCheckout, setIsLoadingPayPalCheckout] = useState(false);
  const [isPaypalExpress, setIsPaypalExpress] = useState(false);
  const [couponRemovedDuringPayment, setCouponRemovedDuringPayment] = useState(false);
  const [isFormRendered, setIsFormRendered] = useState(false);
  const [formCheckCallback, setFormCheckCallback] = useState(null);
  const [isFormError, setIsFormError] = useState(false);
  const [showDvdNotification, setShowDvdNotification] = useState(false);
  const [stepIndex, setStepIndex] = useState(() => determineFirstStep());

  useEffect(() => {
    if (stepIndex === REGISTER_STEP && isLoggedIn) {
      setStepIndex(ADDRESS_STEP);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    setStepIndex(determineFirstStep());
  }, []);

  const handleRequestLogin = (email) => {
    props.onRequestStart && props.onRequestStart();
    RequestLoginMutation.commit(
      props.relay.environment,
      email,
      true,
      props.match.params.language,
      (error, res) => {
        setToLocalStorage('email', email);
        if (!error) {
          props.onOpenNotificationDialog && props.onOpenNotificationDialog(MAGIC_LINK_SENT_MSG);
        } else {
          props.onRequestError && props.onRequestError(error);
        }
        props.onRequestStop && props.onRequestStop();
      }
    );
    trackEvent('Checkout', 'Register', 'Magic Link');
  };

  const handleGoToAddressStep = () => {
    setStepIndex(ADDRESS_STEP);
    setIsFormRendered(true);
  };

  const handleGoToRegisterStep = () => {
    setStepIndex(REGISTER_STEP);
  };

  const handleGoToPaymentStep = useCallback((details) => {
    props.onRequestStart && props.onRequestStart();
    const { order } = cart;
    if (order && (order.state === 'checkout' ||
        order.state === 'payment_in_progress' ||
        order.state === 'payment_failed')) {
      _setOrderAddress(order.id, details);
    } else {
      _createOrder(details);
    }
    trackEvent('Checkout', 'Address', 'Continue to payment step');
  }, [cart, props, _setOrderAddress, _createOrder, trackEvent]);

  const handleCompleteCheckout = (paymentMethod, data, resultCode) => {
    if (isPaypalExpress) {
      _finishPaypalExpressPayment();
    } else {
      switch (paymentMethod.defaultPaymentType) {
        case BANKTRANSFER:
          _payWithBankTransfer();
          return;
        case PAYPAL:
          _payWithPaypal(data);
          return;
        case ADYEN:
          props.onAdyenResult && props.onAdyenResult(data, resultCode);
          return;
        default:
          return;
      }
    }
    trackEvent('Checkout', 'Payment', 'Buy cart items');
  };

  const handleContinueWithPayPalExpress = () => {
    setIsLoadingPayPalCheckout(true);
    const { cart, match, relay } = props;
    BraintreeClientTokenMutation.commit(relay.environment, (error, token) => {
      if (!error) {
        initPaypalButton('paypal-express-checkout-btn', token, {
          checkoutStep: REGISTER_STEP,
          variables: {
            amount: getRealAmount(cart.totalPrice || { amount: 0 }),
            currency: (cart.totalPrice && cart.totalPrice.currency) || 'EUR',
          },
          language: match.params.language,
          onAuthorize: (nonce) => _handlePaypalExpressCheckout(nonce),
          onCancel: () =>
            trackEvent('Checkout', 'Canceled payment', 'PayPal Express payment canceled'),
          onReady: () => {
            setHasPayPalCheckoutBtn(true);
            setIsLoadingPayPalCheckout(false);
          },
        });
      }
    });
    trackEvent('Checkout', 'Register', 'PayPal Express');
  };

  const handleConfirmCheckout = () => {
    const {
      relay,
      cart,
      onRequestStart,
      onRequestStop,
      onCheckoutCompleted,
      onCheckoutFail,
    } = props;
    onRequestStart && onRequestStart();
    ConfirmCheckoutMutation.commit(
      relay.environment,
      cart.order.id,
      (error, updatedOrder) => {
        if (!error) {
          if (updatedOrder.state === 'checkout') {
            onCheckoutFail && onCheckoutFail();
            setCouponRemovedDuringPayment(true);
            onRequestStop && onRequestStop();
          } else {
            onCheckoutCompleted && onCheckoutCompleted(updatedOrder.orderid, cart.customer.email);
          }
        } else {
          onRequestStop && onRequestStop();
        }
      }
    );
  };

  const _handlePaypalExpressCheckout = (nonce) => {
    const { relay, match, cart, onRequestStart, onRequestStop } = props;
    onRequestStart && onRequestStart();
    CreateOrUpdateOrderWithPaypalExpressMutation.commit(
      relay.environment,
      nonce,
      match.params.language,
      cart.id,
      (error, updatedOrder) => {
        if (!error) {
          setIsPaypalExpress(true);
          setStepIndex(PAYMENT_STEP);
        }
        onRequestStop && onRequestStop();
      }
    );
  };

  const _createOrder = (details) => {
    const { relay, match, cart } = props;
    CreateOrderMutation.commit(
      relay.environment,
      details.email,
      match.params.language,
      cart.id,
      (error, updatedOrder) => {
        if (!error) {
          _setOrderAddress(updatedOrder.id, details);
        }
      }
    );
  };

  const _setOrderAddress = (orderId, details) => {
    const { cart, relay } = props;
    SetOrderAddressMutation.commit(
      relay.environment,
      orderId,
      details,
      (error, updatedOrder) => {
        if (!error) {
          if (cart.shipmentRequired) {
            SetOrderShipmentAddressWithNewAddressMutation.commit(
              relay.environment,
              orderId,
              details,
              (error, updatedOrder) => {
                if (!error) {
                  props.onRequestStop && props.onRequestStop();
                  setStepIndex(PAYMENT_STEP);
                }
              }
            );
          } else {
            props.onRequestStop && props.onRequestStop();
            setStepIndex(PAYMENT_STEP);
          }
        }
      }
    );
  };

  const _finishPaypalExpressPayment = () => {
    props.onRequestStart();
    const { cart, relay } = props;
    FinishPaypalExpressPaymentMutation.commit(
      relay.environment,
      cart.order.id,
      (error, updatedOrder) => {
        if (!error) {
          props.onRequestStop && props.onRequestStop();
          props.onCheckoutCompleted && props.onCheckoutCompleted(updatedOrder.orderid, cart.customer.email);
        } else {
          props.onRequestError && props.onRequestError(error);
        }
      }
    );
  };

  const _payWithBankTransfer = () => {
    props.onRequestStart();
    const { cart, relay } = props;
    PayWithBankTransferMutation.commit(
      relay.environment,
      cart.order.id,
      (error, updatedOrder) => {
        if (!error) {
          props.onRequestStop && props.onRequestStop();
          props.onCheckoutCompleted && props.onCheckoutCompleted(updatedOrder.orderid, cart.customer.email);
        } else {
          props.onRequestError && props.onRequestError(error);
        }
      }
    );
  };

  const _payWithPaypal = (nonce) => {
    props.onRequestStart();
    const { cart, relay } = props;
    PayWithPaypalMutation.commit(
      relay.environment,
      cart.order.id,
      nonce,
      (error, updatedOrder) => {
        if (!error) {
          props.onRequestStop && props.onRequestStop();
          props.onCheckoutCompleted && props.onCheckoutCompleted(updatedOrder.orderid, cart.customer.email);
        } else {
          props.onRequestError && props.onRequestError(error);
        }
      }
    );
  };

  const setUpFormCheckCallback = useCallback((callback) => {
    setFormCheckCallback(() => callback);
  }, []);

  const onFormErrorStateChange = useCallback((isError) => {
    setIsFormError(isError);
  }, []);

  const onFormCheck = () => {
    if (formCheckCallback) {
      formCheckCallback();
    }
  };

  const goToCart = () => {
    history.push('/cart');
  };

  const toggleDvdNotification = () => {
    setShowDvdNotification((prev) => !prev);
  };

  return (
    <div className="ordering-steps-container">
      <CartNavigationSteps
        currentStep={stepIndex + 2}
        history={history}
        goToAddressStep={handleGoToAddressStep}
        goToPaymentStep={onFormCheck}
        goToRegisterStep={handleGoToRegisterStep}
        isFormError={isFormError}
        isLoggedInNow={isLoggedIn}
      />
      {isLoading && <LoadingView />}
      {!isLoading && (
        <Fragment>
          {stepIndex === REGISTER_STEP && (
            <CheckoutOrderingRegisterStep
              hasPayPal={hasPayPal}
              hasPayPalCheckoutBtn={hasPayPalCheckoutBtn}
              isLoadingPayPalCheckout={isLoadingPayPalCheckout}
              onContinueAsGuest={handleGoToAddressStep}
              onContinueWithPayPalExpress={handleContinueWithPayPalExpress}
              onRequestLogin={handleRequestLogin}
              onOpenNotificationDialog={onOpenNotificationDialog}
              onPreviousStep={goToCart}
            />
          )}
          {stepIndex === ADDRESS_STEP && (
            <CheckoutOrderingAddressStep
              cart={cart}
              viewer={viewer}
              onNextStep={handleGoToPaymentStep}
              onPreviousStep={onShowCart}
              goToRegisterStep={handleGoToRegisterStep}
              isLoggedInNow={isLoggedIn}
              setUpFormCheckCallback={setUpFormCheckCallback}
              onFormErrorStateChange={onFormErrorStateChange}
              isFormRendered={isFormRendered}
              isFormError={isFormError}
            />
          )}
          {stepIndex === PAYMENT_STEP && (
            <CheckoutOrderingPaymentStep
              cart={cart}
              availablePaymentMethods={cart.availablePaymentMethods}
              paymentRequired={cart.paymentRequired}
              isPaypalExpress={isPaypalExpress}
              onChangeAddress={handleGoToAddressStep}
              onNextStep={handleCompleteCheckout}
              onPreviousStep={handleGoToAddressStep}
              onConfirmCheckout={handleConfirmCheckout}
              couponRemovedDuringPayment={couponRemovedDuringPayment}
              couponShouldBeRemoved={couponShouldBeRemoved}
              onToggleDvdDialog={toggleDvdNotification}
            />
          )}
          <MobileContainer>
            <StickyStayInPlaceContainer
              changingDataShouldUpdateCoords={stepIndex}
              marginTop={30}
            >
              <CartQuickCheckout
                stepNumber={stepIndex}
                onContinueAsGuest={handleGoToAddressStep}
                onGoToPaymentStep={onFormCheck}
                onCompleteCheckout={handleCompleteCheckout}
                isFormError={isFormError}
                isLoggedIn={isLoggedIn}
              />
            </StickyStayInPlaceContainer>
          </MobileContainer>
        </Fragment>
      )}
      <DvdInforationDialog
        open={showDvdNotification}
        onRequestClose={toggleDvdNotification}
        onRequestRedirect={goToCart}
      />
    </div>
  );
};

CheckoutOrderingSteps.propTypes = {
  isLoggedIn: PropTypes.bool,
  isLoading: PropTypes.bool,
  cart: PropTypes.object,
  viewer: PropTypes.object,
  relay: PropTypes.object,
  match: PropTypes.object,
  onCheckoutCompleted: PropTypes.func,
  onShowCart: PropTypes.func,
  onOpenNotificationDialog: PropTypes.func,
  onAdyenResult: PropTypes.func,
  onRequestStart: PropTypes.func,
  onRequestStop: PropTypes.func,
  onRequestError: PropTypes.func,
  onCheckoutFail: PropTypes.func,
  history: PropTypes.object,
  couponShouldBeRemoved: PropTypes.bool,
  isFormRendered: PropTypes.bool,
  apiRedirectToPayment: PropTypes.bool,
};

export { CheckoutOrderingSteps };

export default createFragmentContainer(
  withRouter(withRequestHandler(CheckoutOrderingSteps)),
  {
    cart: graphql`
      fragment CheckoutOrderingSteps_cart on Cart {
        id
        customer {
          email
        }
        lineItems {
          id
          product {
            eventId
          }
        }
        order {
          id
          state
          orderid
        }
        shipmentRequired
        paymentRequired
        totalPrice
        availablePaymentMethods {
          id
          name
          defaultPaymentType
        }

        ...CheckoutOrderingAddressStep_cart
        ...CheckoutOrderingPaymentStep_cart
      }
    `,
    viewer: graphql`
      fragment CheckoutOrderingSteps_viewer on Viewer {
        ...CheckoutOrderingAddressStep_viewer
      }
    `,
  }
);
